import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ArvexisLogo from './ArvexisLogo';

// Preparing the Reflection — Dreams V1 frozen synthesis transition.
// Four temporal states, not backend milestones: no percentages, no countdown, no claim that a
// computational stage finished. The animation never gates completion — if the Reflection is ready
// early we hand over immediately, and if it takes longer we hold on the final state.
const STATES = {
  en: [
    'Staying with what you shared…',
    'Noticing what stood out…',
    'Looking at the feelings and images that stayed with you…',
    'Bringing your Reflection together…',
  ],
  es: [
    'Contemplando lo que compartiste…',
    'Observando lo que más resaltó…',
    'Explorando las emociones y las imágenes que permanecieron contigo…',
    'Dando forma a tu Reflexión…',
  ],
};

// Reference rhythm. Advisory only — real completion always wins.
const ENTERS_AT = [0, 3000, 6000, 9500];

const CHAMPAGNE = '#E6C06E';

const CHROME = {
  en: { eyebrow: 'PREPARING YOUR REFLECTION', privacy: 'Your Dream is private by default.', back: 'Return to Dream Journal' },
  es: { eyebrow: 'PREPARANDO TU REFLEXIÓN', privacy: 'Tu sueño es privado por defecto.', back: 'Volver al Diario de Sueños' },
};

export const PreparingReflection = ({ lang = 'en', ready = false, onHandover, onExit }) => {
  const copy = STATES[lang === 'es' ? 'es' : 'en'];
  const chrome = CHROME[lang === 'es' ? 'es' : 'en'];
  const [index, setIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const handedOver = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const timers = ENTERS_AT.slice(1).map((at, i) =>
      setTimeout(() => setIndex((prev) => Math.max(prev, i + 1)), at)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // Real completion. If it lands before the last state, finish the state we are on gracefully
  // rather than snapping mid-sentence.
  useEffect(() => {
    if (!ready || handedOver.current) return;
    handedOver.current = true;
    setIndex((prev) => Math.max(prev, 3));
    const t = setTimeout(() => onHandover && onHandover(), reducedMotion ? 200 : 900);
    return () => clearTimeout(t);
  }, [ready, onHandover, reducedMotion]);

  return (
    <div
      className="fixed inset-0 z-[60] overflow-hidden"
      data-testid="preparing-reflection"
      data-state-index={index}
      role="status"
      aria-live="polite"
    >
      {/* Shared photographic environment — the frozen production plate, no baked interface */}
      <picture>
        <source media="(min-width: 768px)" srcSet="/assets/dream-recording/plate-desktop.jpg" />
        <img
          src="/assets/dream-recording/plate-mobile.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </picture>
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(8,7,14,0.62) 0%, rgba(8,7,14,0.74) 45%, rgba(8,7,14,0.9) 100%)',
        }}
      />

      <div className="relative h-full overflow-y-auto flex flex-col items-center justify-center px-6 py-10">
        {/* The Arvexis mark inside a thin ring, with two very slow orbital arcs */}
        <div className="relative mb-9" style={{ width: 216, height: 216 }}>
          <motion.span
            className="absolute inset-[10%] rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(230,192,110,0.13) 0%, rgba(230,192,110,0.04) 58%, transparent 74%)',
            }}
            animate={reducedMotion ? { opacity: 0.6 } : { opacity: [0.5, 0.95, 0.5], scale: [1, 1.05, 1] }}
            transition={reducedMotion ? { duration: 0 } : { duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <span
            className="absolute inset-[16%] rounded-full pointer-events-none"
            style={{ border: '1px solid rgba(230,192,110,0.30)' }}
          />
          {[
            { d: 'M 108 12 A 96 96 0 0 1 196 74', dur: 46, dot: [196, 74] },
            { d: 'M 22 148 A 96 96 0 0 0 150 202', dur: 62, dot: [22, 148] },
          ].map((arc, i) => (
            <motion.svg
              key={i}
              viewBox="0 0 216 216"
              className="absolute inset-0 w-full h-full pointer-events-none"
              animate={reducedMotion ? { rotate: 0 } : { rotate: i === 0 ? 360 : -360 }}
              transition={reducedMotion ? { duration: 0 } : { duration: arc.dur, repeat: Infinity, ease: 'linear' }}
            >
              <path d={arc.d} fill="none" stroke="rgba(230,192,110,0.55)" strokeWidth="1" strokeLinecap="round" />
              <circle cx={arc.dot[0]} cy={arc.dot[1]} r="2.6" fill="#E6C06E" />
            </motion.svg>
          ))}
          <div className="absolute inset-0 flex items-center justify-center">
            <ArvexisLogo size={72} />
          </div>
        </div>

        <p
          className="text-[10px] md:text-[11px] mb-4"
          style={{ color: 'rgba(255,255,255,0.42)', letterSpacing: '0.34em' }}
          data-testid="preparing-eyebrow"
        >
          {chrome.eyebrow}
        </p>

        {/* Copy dissolves softly between states */}
        <div className="min-h-[124px] md:min-h-[112px] flex items-start justify-center w-full">
          <AnimatePresence mode="wait">
            <motion.p
              key={index}
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
              transition={{ duration: reducedMotion ? 0.2 : 0.85, ease: 'easeOut' }}
              className="text-center"
              style={{
                color: 'rgba(255,255,255,0.94)',
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: 'clamp(28px, 5.4vw, 44px)',
                lineHeight: 1.24,
                maxWidth: '20ch',
              }}
              data-testid="preparing-reflection-copy"
            >
              {copy[index]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* All four states stay visible so the progression is legible: a connected horizontal
            track on desktop, a vertical list on mobile. Not a progress bar — no percentage, and
            no claim that a computational stage finished. */}
        <div className="hidden md:flex items-start justify-center mt-10" data-testid="preparing-reflection-track">
          {copy.map((label, i) => (
            <div key={i} className="flex items-start">
              {i > 0 && (
                <motion.span
                  className="block h-px mt-[7px]"
                  style={{ width: 96, background: CHAMPAGNE }}
                  initial={{ opacity: 0.12 }}
                  animate={{ opacity: i <= index ? 0.5 : 0.12 }}
                  transition={{ duration: reducedMotion ? 0.2 : 1.1 }}
                />
              )}
              <div className="flex flex-col items-center" style={{ width: 172 }}>
                <span className="relative flex items-center justify-center" style={{ width: 16, height: 16 }}>
                  {i === index && (
                    <motion.span
                      className="absolute rounded-full"
                      style={{ width: 16, height: 16, border: `1px solid ${CHAMPAGNE}` }}
                      animate={reducedMotion ? { opacity: 0.9 } : { opacity: [0.55, 1, 0.55] }}
                      transition={reducedMotion ? { duration: 0 } : { duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  )}
                  <span
                    className="block rounded-full"
                    style={{
                      width: 7,
                      height: 7,
                      background: CHAMPAGNE,
                      opacity: i < index ? 0.75 : i === index ? 1 : 0.22,
                    }}
                  />
                </span>
                <p
                  className="mt-3 text-center text-[13px] leading-snug px-2"
                  style={{ color: i === index ? CHAMPAGNE : 'rgba(255,255,255,0.4)' }}
                >
                  {label}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="md:hidden mt-8 w-full max-w-[22rem] space-y-3.5" data-testid="preparing-reflection-list">
          {copy.map((label, i) => (
            <p
              key={i}
              className="text-center leading-snug"
              style={
                i === index
                  ? {
                      color: CHAMPAGNE,
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontSize: 19,
                    }
                  : { color: 'rgba(255,255,255,0.34)', fontSize: 15 }
              }
            >
              {label}
            </p>
          ))}
        </div>

        <span className="block mt-9 mb-5" style={{ width: 46, height: 1, background: 'rgba(230,192,110,0.6)' }} />

        <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.45)' }} data-testid="preparing-privacy">
          {chrome.privacy}
        </p>

        {onExit && (
          <button
            type="button"
            onClick={onExit}
            className="mt-6 inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-sm transition-colors"
            style={{ border: '1px solid rgba(255,255,255,0.16)', color: 'rgba(255,255,255,0.7)' }}
            data-testid="preparing-return-btn"
          >
            <span aria-hidden="true">←</span>
            {chrome.back}
          </button>
        )}
      </div>
    </div>
  );
};

export default PreparingReflection;
